import { canBrowseAlbum } from '../../../../app/features';
import { GetCredentialInteractor } from '../../../auth/domain/interactors/get-credential.interactor';
import { StorageListPage } from '../../data/common/list-page';
import { MockItem } from '../../data/common/mock.item';
import { MockListPage } from '../../data/common/mock.list-page';
import { StorageDialogService } from '../../storage-dialog.service';
import { StorageProvider } from '../../storage.provider';
import { Album, AlbumStatus, StorageItem } from '@uploadcam/common';

const ROOT_KEY = 'uploadcam://albums-root';

export interface AlbumItemMetadata {
  isAlbum: boolean;
  albumId: string;
  albumName: string;
}

export class ShowAlbumOrSubfolderPickerInteractor {
  private readonly canBrowse = canBrowseAlbum();

  constructor(
    private readonly getCredential: GetCredentialInteractor,
    private readonly storageDialog: StorageDialogService,
    private readonly storageProvider: StorageProvider,
  ) {}

  public async execute(
    albums: readonly Album[],
    albumCtx?: Album,
  ): Promise<StorageItem | undefined> {
    const credential = this.getCredential.execute();
    const manager = await this.storageProvider.provideManager();
    const root = albumCtx
      ? MockItem.fromStorageFolder(albumCtx.folder, undefined, undefined)
      : undefined;

    return this.storageDialog.openSelectFolderDialog({
      title: root
        ? 'Select Sub-folder'
        : this.canBrowse.result === 'yes'
          ? 'Select Folder or Sub-folder'
          : 'Select Folder',
      config: {
        root: root ?? MockItem.rootFolder(ROOT_KEY, credential.provider, undefined, false),
        canBrowse: (_item) => true,
        isDisabled: (_currentFolder, _item) => ({ state: false }),
        listFolder: async (item, onlyFolders) => {
          if (item.id === ROOT_KEY) {
            return new MockListPage(
              albums
                .filter((album) => album.systemStatus === AlbumStatus.Active)
                .map((album) =>
                  MockItem.folder(
                    album.folder.id,
                    album.folder.provider,
                    album.folder.driveId,
                    album.name,
                    false,
                    {
                      isAlbum: true,
                      albumId: album.id,
                      albumName: album.name,
                    },
                  ),
                ),
            );
          } else {
            const listPage = await manager.listFolder(item, onlyFolders);

            return new StorageListPageMetadataDecorator(listPage, (i) => ({
              ...i.metadata,
              isAlbum: false,
              albumId: item.metadata['albumId'],
              albumName: item.metadata['albumName'],
            }));
          }
        },
        setSelected: (selected) => (selected?.id !== (root?.id ?? ROOT_KEY) ? selected : undefined),
        showCreateFolder: (_folder) => false,
      },
    });
  }
}

class StorageListPageMetadataDecorator implements StorageListPage {
  constructor(
    private readonly decorated: StorageListPage,
    private readonly metadataFn: (item: StorageItem) => AlbumItemMetadata,
  ) {}

  public getItems(): StorageItem[] {
    return this.decorated.getItems().map((item) => {
      return new Proxy(item, {
        get: (target, prop, receiver) => {
          if (prop === 'metadata') {
            return this.metadataFn(target);
          }

          return Reflect.get(target, prop, receiver);
        },
      });
    });
  }

  public hasNext(): boolean {
    return this.decorated.hasNext();
  }

  public getNext(): Promise<StorageListPage> {
    return this.decorated.getNext();
  }
}
